import {
  IMetarData,
  ITafData,
  ITafForecast,
  IWeatherForecastData,
} from "@/interfaces/Map";
import { Nullable } from "./types";
import { formatCoordinates } from "@/helpers/unit-conversions";

export const CURRENT_YEAR = new Date().getFullYear();

export const PAGE_TITLE = "NOAA Aviation Weather";
export const COPYRIGHT_TXT = `Copyright © ${CURRENT_YEAR} Aviation Weather Inc.`;

export const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=org.nextstack.aviation";
export const APP_STORE_URL =
  "https://apps.apple.com/am/app/aviation-weather-metar-taf/id1445138836";

export const PAGE_TABS = {
  about: "About",
  features: "Features",
  review: "Review",
  faq: "FAQ",
  terms: "terms",
  privacy: "Privacy",
  map: "Map",
  see_more: "See More",
  qr: "QR",
} as const;

export const HEADER_MENU = [
  {
    id: 1,
    title: "Map",
    path: "/",
  },
  {
    id: 2,
    title: "Terms",
    path: "/terms-and-conditions",
  },
  {
    id: 3,
    title: "Privacy",
    path: "/privacy-policy",
  },
];

export const PATHNAMES = {
  home: "/",
  terms: "/terms-and-conditions",
  privacy: "/privacy-policy",
  verify: "/verify",
  resetPassword: "/reset-password",
} as const;

export const PATHNAMES_WITHOUT_HEADER = [
  PATHNAMES.verify,
  PATHNAMES.resetPassword,
];

export const AMPLITUDE_API_KEY = "9d2b8c2566dd381bb4b6659db210de79";

export const MAP = {
  WIND_SPEED: "Wind Speed",
  PRESSURE: "Pressure",
  TEMPERATURE: "Temperature",
  CLOUDS: "Clouds",
  DIRECTION: "Direction",
  HUMIDITY: "Humidity",
  PRECIPITATION: "Precipitation",
  SEARCH: "Search",
  NOT_FOUND: "Nothing Found",
  NO_MATCHING:
    "No matching stations or addresses were found. Please try a different search.",
  MIN: "Min",
  MAX: "Max",
  OBSERVATION_DATE: "Observation Date",
  TODAY_AT: "Today at",
  SEE_MORE: "see more",
  HPA: "hPa",
  M_S: "m/s",
  WIND_NEW: "wind_new",
  PRESSURE_NEW: "pressure_new",
  TEMP_NEW: "temp_new",
  CLOUDS_NEW: "clouds_new",
  PRECIPITATION_NEW: "precipitation_new",
  WEATHER_LAYER: "styled_map",
  ROADMAP: "roadmap",
  IMPERIAL: "imperial",
  METRIC: "metric",
} as const;

export const LOCAL = {
  DOWNLOAD_MARINE_WEATHER: "Download Aviation Weather on iOS and Android",
  WELCOME_MARINE_WEATHER:
    "Welcome to Aviation Weather, your go-to app for all your flight planning needs!",
  ELEVATE_ADVENTURES: "Elevate adventures with charts and maps.",
  DOWNLOAD_ACCURATE: "Download for accurate forecasts and updates.",
  LEVEL_UP: "Level up with customized settings and widgets.",
  ENHANCE_EXPERIENCES: "Enhance your experiences; stay safe and informed.",
  SCAN_QR: "Scan QR Code to Download",
  OPEN_LINK: "Open Link",
} as const;

export const TAP_STORE = "Tap Store";
export const APP_STORE = "app-store";
export const IOS = "Ios";
export const GOOGLE = "Google";
export const ANDROID = "Android";
export const PAGE_VIEW = "Page View";
export const FEATURES = "Features";
export const TAP_APP = "Tap App";
export const MAP_SEARCH = "Map search";
export const TIDE = "Tide";
export const OPEN_MARKER = "Open marker";
export const CHANGE_TILE = "Change tile";

export interface ILayer {
  id: string;
  name: string;
  action: string;
  src: string;
}

export const LAYERS: ILayer[] = [
  {
    id: "1",
    name: "wind",
    action: MAP.WIND_NEW,
    src: "/images/wind.svg",
  },
  {
    id: "2",
    name: "pressure",
    action: MAP.PRESSURE_NEW,
    src: "/images/pressure.svg",
  },
  {
    id: "3",
    name: "temperature",
    action: MAP.TEMP_NEW,
    src: "/images/temperature.svg",
  },
  {
    id: "4",
    name: "clouds",
    action: MAP.CLOUDS_NEW,
    src: "/images/clouds.svg",
  },
  {
    id: "5",
    name: "precipitation",
    action: MAP.PRECIPITATION_NEW,
    src: "/images/precipitation.svg",
  },
];

export const CURRENT_LOCATION_ZOOM = 11;

export const GENERAL = {
  STATION_FETCH_SIZE: 2000,
  BATCH_SIZE: 500,
  BOUNDS_PADDING_PERCENT: 0.1,
  GEOLOCATION_TIMEOUT: 5000,
  ANIMATION_TIMEOUT: 1000,
  CLOSE_ANIMATION_DELAY: 500,
  POPUP_CLOSE_ANIMATION_DELAY: 300,
  MAP_DEBOUNCE_TIMEOUT: 800,
  INITIAL_ICON_SIZE: 50,
  FORECAST_DETAILS_FIRST_SECTION_COUNT: 5,
  SKELETON_COUNT: 8,
  TOAST_AUTO_HIDE_DURATION: 5000,
  TIME_FORMAT: "YYYY-MM-DD hh:mm:ss",
  DATE_FORMAT: "DD.MM.YYYY",
  TIME_ONLY_FORMAT: "HH:mm",
  IMAGE_SIZES: {
    SMALL: 8,
    MEDIUM: 16,
    LARGE: 20,
    EXTRA_LARGE: 24,
    MOBILE_ICON: 40,
    LOGO_WIDTH: 125,
    LOGO_HEIGHT: 20,
    LOGO_LARGE_WIDTH: 202,
    LOGO_LARGE_HEIGHT: 32,
    NO_DATA_WIDTH: 42,
    NO_DATA_HEIGHT: 42,
  },
} as const;

export const CONFIG = {
  API: {
    DEFAULT_PAGE: 1,
    QUERY_PARAMS: {
      FORMAT: "json",
      TAF: "true",
    },
  },
  QR_ORIGIN: {
    DEFAULT: "google-play" as const,
    GOOGLE_PLAY: "google-play" as const,
    APP_STORE: "app-store" as const,
  },
  RAW_DATA: {
    ICAO_CODE_INDEX: 2,
    SPLIT_DELIMITER: " ",
  },
  MARKER_CLUSTERER: {
    GRID_SIZE_FAR: 400,
    GRID_SIZE_NEAR: 200,
    MAX_ZOOM: 15,
    MINIMUM_CLUSTER_SIZE: 2,
    ZOOM_ON_CLICK: true,
    TEXT_COLOR: "white",
  },
} as const;

export const UI_TEXT = {
  TABS: {
    METAR: "Metar",
    TAF: "TAF",
  },
  LABELS: {
    RAW_METAR: "Raw METAR",
    RAW_TAF: "Raw TAF",
    LEARN_MORE: "Download app for more info",
    NO_INFORMATION_AVAILABLE: "No Information Available",
    NO_INFORMATION_DESCRIPTION:
      "There is no available information for this station.",
  },
  POPUP: {
    DOWNLOAD_TITLE: "Download Aviation Weather on iOS and Android",
    DOWNLOAD_DESCRIPTION:
      "Welcome to Aviation Weather, your go-to app for all your flight planning needs!",
    SCAN_QR_TITLE: "Scan QR Code to Download",
    OPEN_LINK: "Open Link",
  },
  MESSAGES: {
    ACCESS_LIVE_DATA: "Access live METARs, TAFs, and maps in one place.",
    OPTIMIZE_ROUTE: "Optimize your route with wind and weather insights.",
    STAY_FLIGHT_READY: "Stay flight-ready with up-to-the-minute NOTAMs",
    MONITOR_ATIS: "Monitor ATIS data instantly for all your key stations",
  },
  ERRORS: {
    GEOLOCATION_NOT_SUPPORTED: "Geolocation is not supported by this browser.",
    GEOLOCATION_TRACKING_ERROR: "Error tracking location.",
    FAILED_FETCH_METAR: "Failed to fetch METAR data",
    FAILED_FETCH_TAF: "Failed to fetch TAF data",
    FAILED_FETCH_WEATHER_FORECAST: "Failed to fetch weather forecast data",
    FAILED_FETCH_STATIONS: "Failed to fetch stations for map",
    FAILED_FETCH_STATION: "Failed to fetch selected station",
    MAP_API_KEY_NOT_CONFIGURED: "Google Maps API key is not configured",
    MAP_LOAD_FAILED: "Failed to load Google Maps. Check API key and domain restrictions.",
    MAP_ERROR_DESCRIPTION: "Check browser console for more details. Verify that:",
    MAP_ERROR_API_KEY: "NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is set in production",
    MAP_ERROR_DOMAIN: "API key has correct domain restrictions",
    MAP_ERROR_APIS_ENABLED: "Required Google Maps APIs are enabled",
  },
} as const;

export const IMAGE_PATHS = {
  ICONS: {
    CLOSE: "/icon/close.svg",
    BACK_ARROW: "/back-arrow.svg",
    TRIANGLE: "/triangle.svg",
    NO_DATA: "/no-data.svg",
    LAYERS: "/images/layers.svg",
    LOCATION: "/images/location.svg",
    PLUS: "/images/plus.svg",
    MINUS: "/images/minus.svg",
    SEARCH: "/images/search.svg",
    CLEAR: "/images/Clear.svg",
  },
  LOGOS: {
    AVIATION_LOGO: "/icon/markets/aviation-logo.svg",
  },
  POPUP: {
    DOWNLOAD_IMAGE: "/downloadImage.svg",
    DOWNLOAD_IMAGE_MOBILE: "/downloadImageMobile.svg",
  },
} as const;

export const MetarDetailsItems = [
  {
    iconSrc: "/icons/clock.svg",
    label: "Observation Time",
    getValue: (activeMetar: IMetarData | null) =>
      activeMetar?.observationTime || "",
  },
  {
    iconSrc: "/icons/elevation.svg",
    label: "Station Elevation",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.stationElevation ?? ""} ${
        activeMetar?.stationElevationUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/pressure.svg",
    label: "Pressure",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.pressure ?? ""} ${
        activeMetar?.pressureUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/skyCover.svg",
    label: "Sky Cover",
    getValue: (activeMetar: IMetarData | null) => activeMetar?.skyCover || "",
  },
  {
    iconSrc: "/icons/verticalVis.svg",
    label: "Vertical Visibility",
    getValue: () => "-",
  },
  {
    iconSrc: "/icons/horizontalVis.svg",
    label: "Horizontal Visibility",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.horizontalVisibility ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/altimeter.svg",
    label: "Altimeter",
    getValue: () => "-",
  },
  {
    iconSrc: "/icons/temperature.svg",
    label: "Temperature",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.temperature ?? ""} ${
        activeMetar?.temperatureUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/weather.svg",
    label: "Weather",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.weather ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/windDir.svg",
    label: "Wind Direction Range",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.windDegrees ?? ""}${
        activeMetar?.windDirectionUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/windGust.svg",
    label: "Wind Speed",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.windSpeed ?? ""} ${
        activeMetar?.windSpeedUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/windSpeed.svg",
    label: "Wind Speed",
    getValue: (activeMetar: IMetarData | null) =>
      `${activeMetar?.windSpeed ?? ""} ${activeMetar?.windSpeedUnit ?? "-"}, ${
        activeMetar?.windDegrees ?? ""
      } ${activeMetar?.windDirectionUnit ?? ""}`.trim(),
  },
];

export const TafDetailsItems = [
  {
    iconSrc: "/icons/elevation.svg",
    label: "Station Elevation",
    getValue: (tafData: ITafData | null) =>
      `${tafData?.stationElevation ?? ""} ${
        tafData?.stationElevationUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/bulletinTime.svg",
    label: "Bulletin Time",
    getValue: (tafData: ITafData | null) => tafData?.bulletinTime || "",
  },
  {
    iconSrc: "/icons/validTime.svg",
    label: "Valid Time From",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.validTimeFrom || "",
  },
  {
    iconSrc: "/icons/validTime.svg",
    label: "Valid Time To",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.validTimeTo || "",
  },
  {
    iconSrc: "/icons/changeIndicator.svg",
    label: "Change Indicator",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.changeIndicator || "-",
  },
  {
    iconSrc: "/icons/clock.svg",
    label: "Time Becoming",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.timeBecoming || "-",
  },
  {
    iconSrc: "/icons/verticalVis.svg",
    label: "Vertical Visibility",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => `${activeForecast?.verticalVisibility ?? "-"}`.trim(),
  },
  {
    iconSrc: "/icons/horizontalVis.svg",
    label: "Horizontal Visibility",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => `${activeForecast?.horizontalVisibility ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/cloudBase.svg",
    label: "Cloud Base",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) =>
      `${activeForecast?.cloudBase ?? ""} ${
        activeForecast?.cloudBaseUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/skyCover.svg",
    label: "Sky Cover",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.skyCover || "",
  },
  {
    iconSrc: "/icons/weather.svg",
    label: "Weather",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) => activeForecast?.weather || "",
  },
  {
    iconSrc: "/icons/windDir.svg",
    label: "Wind Direction",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) =>
      `${activeForecast?.windDegrees ?? ""}${
        activeForecast?.windDirectionUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/windGust.svg",
    label: "Wind Gust",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) =>
      `${activeForecast?.windSpeed ?? ""} ${
        activeForecast?.windSpeedUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/windSpeed.svg",
    label: "Wind Speed",
    getValue: (
      _tafData: ITafData | null,
      activeForecast: ITafForecast | null
    ) =>
      `${activeForecast?.windSpeed ?? ""} ${
        activeForecast?.windSpeedUnit ?? ""
      }, ${activeForecast?.windDegrees ?? ""} ${
        activeForecast?.windDirectionUnit ?? ""
      }`.trim(),
  },
];

export const ForecastDetailsItems = [
  {
    label: "Category",
    getValue: () => "",
  },
  {
    label: "Coordinates",
    getValue: (forcast: IWeatherForecastData | null) => {
      return formatCoordinates(
        forcast?.latitude || null,
        forcast?.longitude || null
      );
    },
  },
  {
    label: "Elevation",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.elevation ?? ""} ${forecast?.elevationUnit ?? ""}`.trim(),
  },
  {
    label: "Pressure Altitude",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.pressureAltitude} ${forecast?.pressureAltitudeUnit}`,
  },
  {
    label: "Density Altitude",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.densityAltitude} ${forecast?.densityAltitudeUnit}`,
  },
  {
    iconSrc: "/icons/sunrise.svg",
    label: "Sunrise",
    getValue: (
      forecast?: IWeatherForecastData,
      formatTimeFn?: (dateString: string | undefined) => string
    ) => {
      if (!forecast?.sunrise) return "";
      return formatTimeFn ? formatTimeFn(forecast.sunrise) : "";
    },
  },
  {
    iconSrc: "/icons/sunset.svg",
    label: "Sunset",
    getValue: (
      forecast: IWeatherForecastData | null,
      formatTimeFn?: (dateString: string | undefined) => string
    ) => {
      if (!forecast?.sunset) return "";
      return formatTimeFn ? formatTimeFn(forecast.sunset) : "";
    },
  },
  {
    iconSrc: "/icons/moonrise.svg",
    label: "Moonrise",
    getValue: (
      forecast: IWeatherForecastData | null,
      formatTimeFn?: (dateString: string | undefined) => string
    ) => {
      if (!forecast?.moonrise) return "";
      return formatTimeFn ? formatTimeFn(forecast.moonrise) : "";
    },
  },
  {
    iconSrc: "/icons/moonset.svg",
    label: "Moonset",
    getValue: (
      forecast: IWeatherForecastData | null,
      formatTimeFn?: (dateString: string | undefined) => string
    ) => {
      if (!forecast?.moonset) return "";
      return formatTimeFn ? formatTimeFn(forecast.moonset) : "";
    },
  },
];

export const ForecastMetarDetailsItems = [
  {
    iconSrc: "/icons/temperature.svg",
    label: "Temperature",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.temperature ?? ""} ${
        forecast?.temperatureUnit ?? ""
      }`.trim(),
  },
  {
    iconSrc: "/icons/weather.svg",
    label: "Weather",
    getValue: (forecast: IWeatherForecastData | null) =>
      forecast?.weatherCondition || "",
  },
  {
    iconSrc: "/icons/pressure.svg",
    label: "Pressure",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.pressure ?? ""} ${forecast?.pressureUnit ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/dewPoint.svg",
    label: "Dewpoint",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.dewPoint ?? ""} ${forecast?.dewPointUnit ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/uvIndex.svg",
    label: "UV Index",
    getValue: (forecast: IWeatherForecastData | null) =>
      forecast?.uvIndex?.toString() || "",
  },
  {
    iconSrc: "/icons/visibility.svg",
    label: "Visibility",
    getValue: (forecast: IWeatherForecastData | null) =>
      `${forecast?.visibility ?? ""} ${forecast?.visibilityUnit ?? ""}`.trim(),
  },
  {
    iconSrc: "/icons/windSpeed.svg",
    label: "Wind Speed and Direction",
    getValue: (
      forecast: IWeatherForecastData | null,
      _formatTimeFn?: (dateString: string | undefined) => string,
      formatWindSpeedFn?: (
        speed: Nullable<number>,
        unit: Nullable<string>
      ) => string
    ) => {
      if (!forecast?.windSpeed || !forecast?.windSpeedUnit) return "";
      const windSpeed = formatWindSpeedFn
        ? formatWindSpeedFn(forecast.windSpeed, forecast.windSpeedUnit)
        : `${forecast.windSpeed} ${forecast.windSpeedUnit}`;
      return `${windSpeed}, ${forecast?.windDirection ?? ""}${
        forecast?.windDirectionUnit ?? ""
      }`.trim();
    },
  },
];
